import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Disc3, ChevronRight, Music, Pencil, AlertTriangle, X, ExternalLink, Search, Radio, Trash2, Users, LayoutList, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { getReleases, getReleaseDetail, getAdminReleases, deleteMyRelease } from "@/lib/afmApi";
import { fmtDate } from "@/lib/format";
import { Loading, EmptyState, ErrorState } from "@/components/common";
import { StatusBadge, RELEASE_STATUS, RELEASE_GROUPS, releaseGroup } from "@/components/StatusBadge";
import TrackPanel from "@/components/TrackPanel";

const FILTERS = [
  { key: "", label: "Todos" },
  { key: "draft", label: "Borradores" },
  { key: "review", label: "En revisión" },
  { key: "action", label: "Acción requerida" },
  { key: "live", label: "En tiendas" },
];

const ADMIN_ROLES = ["admin", "distribution_admin", "superadmin", "super_admin", "staff"];
// "Dey f, Lakrozo" / "A & B" / "A x B" -> ["Dey f", "Lakrozo"]
const artistsOf = (r) => String(r.main_artist || r.artist_name || "Sin artista")
  .split(/\s*(?:,|&|\s+x\s+|\s+feat\.?\s+|\s+ft\.?\s+)\s*/i).map((x) => x.trim()).filter(Boolean);
const keyOf = (n) => n.trim().toLowerCase();

export default function Catalog() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [releases, setReleases] = useState([]);
  const [status, setStatus] = useState("loading");
  const [filter, setFilter] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState(null); // { id, title, text, loading }
  const isAdmin = ADMIN_ROLES.includes(user?.role) || !!user?.super_admin;
  const [scope, setScope] = useState("mine"); // mine | all (solo el sello)
  const [view, setView] = useState("list"); // list | artists | accounts
  const [artist, setArtist] = useState(""); // filtro por artista (clave)
  const [account, setAccount] = useState(""); // filtro por cuenta (solo "todo el sello")

  const load = () => {
    setStatus("loading");
    const req = scope === "all" ? getAdminReleases(user.id, "").then((d) => d?.releases || []) : getReleases(user.id);
    req.then((d) => { setReleases(Array.isArray(d) ? d : []); setStatus("ok"); })
      .catch(() => setStatus("error"));
  };
  useEffect(load, [user.id, scope]); // eslint-disable-line react-hooks/exhaustive-deps

  // Agrupado por artista (un lanzamiento con dos artistas sale en los dos) y por cuenta.
  const groups = useMemo(() => {
    const by = new Map();
    releases.forEach((r) => {
      const names = view === "accounts" ? [r.artist_name || r.user_name || "Sin cuenta"] : artistsOf(r);
      names.forEach((n) => {
        const k = keyOf(n);
        if (!by.has(k)) by.set(k, { key: k, name: n, total: 0, live: 0, review: 0, action: 0, draft: 0, cover: "" });
        const g = by.get(k);
        g.total += 1; g[releaseGroup(r.status)] = (g[releaseGroup(r.status)] || 0) + 1;
        if (!g.cover && r.cover_url) g.cover = r.cover_url;
      });
    });
    return [...by.values()].sort((a, b) => b.live - a.live || b.total - a.total || a.name.localeCompare(b.name));
  }, [releases, view]);

  const removeRelease = async (r, e) => {
    e.stopPropagation();
    if (!window.confirm(`¿Eliminar "${r.title}"? Solo se puede si nunca llegó a la distribuidora. Desaparece del catálogo.`)) return;
    try {
      await deleteMyRelease(r.id);
      toast.success("Lanzamiento eliminado");
      setReleases((p) => p.filter((x) => x.id !== r.id));
    } catch (er) { toast.error(er?.response?.data?.detail || "No se pudo eliminar"); }
  };

  // Los contadores siguen al artista/cuenta elegido.
  const pool = useMemo(() => releases.filter((r) =>
    (!artist || artistsOf(r).some((n) => keyOf(n) === artist))
    && (!account || keyOf(r.artist_name || r.user_name || "Sin cuenta") === account)), [releases, artist, account]); // eslint-disable-line react-hooks/exhaustive-deps
  const counts = useMemo(() => {
    const c = { draft: 0, review: 0, action: 0, live: 0 };
    pool.forEach((r) => { c[releaseGroup(r.status)] = (c[releaseGroup(r.status)] || 0) + 1; });
    return c;
  }, [pool]);

  const shown = useMemo(() => {
    let list = filter ? releases.filter((r) => releaseGroup(r.status) === filter) : releases;
    if (artist) list = list.filter((r) => artistsOf(r).some((n) => keyOf(n) === artist));
    if (account) list = list.filter((r) => keyOf(r.artist_name || r.user_name || "Sin cuenta") === account);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((r) => `${r.title} ${r.main_artist || r.artist_name || ""} ${r.genre_main || ""} ${r.isrc || ""}`.toLowerCase().includes(q));
    return list;
  }, [releases, filter, query, artist, account]);

  const openNote = async (r) => {
    setNote({ id: r.id, title: r.title, text: "", loading: true });
    try {
      const d = await getReleaseDetail(r.id);
      setNote({ id: r.id, title: r.title, text: d.admin_note || d.note || d.correction_note || "", loading: false });
    } catch {
      setNote({ id: r.id, title: r.title, text: "", loading: false });
    }
  };

  if (status === "loading") return <Loading />;
  if (status === "error") return <ErrorState onRetry={load} />;
  if (!releases.length && scope === "mine" && !isAdmin)
    return <EmptyState icon={Disc3} title="Sin lanzamientos" desc="Tus lanzamientos aparecerán aquí con su estado de distribución." testId="catalog-empty" />;

  return (
    <div className="space-y-5" data-testid="catalog-page">
      {/* Search explorer */}
      <div className="relative" data-testid="catalog-search">
        <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          data-testid="catalog-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Busca un track por título, artista, género o ISRC…"
          className="w-full rounded-xl border border-slate-300 bg-white py-3 pl-11 pr-4 text-sm outline-none transition-colors focus:border-black"
        />
      </div>

      {/* Vista: lanzamientos / por artista / por cuenta · y alcance (mío / todo el sello) */}
      <div className="flex flex-wrap items-center justify-between gap-3" data-testid="catalog-views">
        <div className="flex rounded-xl border border-slate-200 bg-white p-1">
          {[["list", "Lanzamientos", LayoutList], ["artists", "Por artista", Users], ...(scope === "all" ? [["accounts", "Por cuenta", Building2]] : [])].map(([k, l, Icon]) => (
            <button key={k} data-testid={`catalog-view-${k}`} onClick={() => setView(k)}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${view === k ? "bg-black text-white" : "text-slate-600 hover:text-black"}`}>
              <Icon className="h-3.5 w-3.5" /> {l}
            </button>
          ))}
        </div>
        {isAdmin && (
          <div className="flex rounded-xl border border-slate-200 bg-white p-1" data-testid="catalog-scope">
            {[["mine", "Mis lanzamientos"], ["all", "Todo el sello"]].map(([k, l]) => (
              <button key={k} onClick={() => { setScope(k); setArtist(""); setAccount(""); if (k === "mine" && view === "accounts") setView("artists"); }}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${scope === k ? "bg-[#AC3333] text-white" : "text-slate-600 hover:text-black"}`}>{l}</button>
            ))}
          </div>
        )}
      </div>

      {(artist || account) && (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="catalog-active-filter">
          <span className="text-slate-500">Mostrando:</span>
          <button onClick={() => { setArtist(""); setAccount(""); }} className="flex items-center gap-1.5 rounded-full border border-black bg-black px-3 py-1 font-semibold text-white">
            {(groups.find((g) => g.key === (artist || account)) || {}).name || artist || account} <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {view !== "list" && !artist && !account ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalog-groups">
          {groups.map((g) => (
            <button key={g.key} onClick={() => { if (view === "accounts") setAccount(g.key); else setArtist(g.key); setView("list"); }}
              className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 text-left transition-all hover:border-black hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)]" data-testid={`group-${g.key}`}>
              <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-slate-100">
                {g.cover ? <img src={g.cover} alt="" className="h-full w-full object-cover" /> : view === "accounts" ? <Building2 className="h-5 w-5 text-slate-400" /> : <Users className="h-5 w-5 text-slate-400" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-serif text-base font-bold text-black">{g.name}</div>
                <div className="text-xs text-slate-500">{g.total} lanzamiento{g.total === 1 ? "" : "s"}</div>
                <div className="mt-1.5 flex flex-wrap gap-1.5 text-[10px] font-semibold">
                  {g.live > 0 && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700">{g.live} en tiendas</span>}
                  {g.review > 0 && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-blue-700">{g.review} en revisión</span>}
                  {g.action > 0 && <span className="rounded-full bg-red-50 px-2 py-0.5 text-red-700">{g.action} con acción</span>}
                  {g.draft > 0 && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-700">{g.draft} borrador{g.draft === 1 ? "" : "es"}</span>}
                </div>
              </div>
              <ChevronRight className="h-5 w-5 shrink-0 text-slate-300" />
            </button>
          ))}
          {!groups.length && <p className="rounded-xl border border-dashed border-slate-300 bg-white px-5 py-10 text-center text-sm text-slate-400 sm:col-span-2 lg:col-span-3">Sin lanzamientos.</p>}
        </div>
      ) : (<>
      {/* Filter / legend */}
      <div className="flex flex-wrap gap-2" data-testid="catalog-filters">
        {FILTERS.map((ff) => {
          const n = ff.key ? counts[ff.key] : pool.length;
          const dot = RELEASE_GROUPS[ff.key]?.dot;
          const active = filter === ff.key;
          return (
            <button
              key={ff.key || "all"}
              data-testid={`catalog-filter-${ff.key || "all"}`}
              onClick={() => setFilter(ff.key)}
              className={`flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${active ? "border-black bg-black text-white" : "border-slate-300 bg-white text-slate-600 hover:border-black"}`}
            >
              {dot && <span className="h-2 w-2 rounded-full" style={{ backgroundColor: dot }} />}
              {ff.label}
              <span className={`font-mono tabular-nums ${active ? "text-white/70" : "text-slate-400"}`}>{n}</span>
            </button>
          );
        })}
      </div>

      <div className="space-y-3">
        {shown.map((r) => {
          const grp = releaseGroup(r.status);
          const bar = RELEASE_GROUPS[grp]?.bar || "bg-slate-300";
          const isAction = grp === "action";
          const isDraft = grp === "draft";
          const isLive = grp === "live";
          const primary = () => (isAction ? openNote(r) : isDraft ? navigate("/distribute") : isLive ? setSelected(r) : navigate(`/catalog/${r.id}`));
          return (
            <div
              key={r.id}
              data-testid={`release-card-${r.id}`}
              onClick={primary}
              className="group flex cursor-pointer items-stretch gap-0 overflow-hidden rounded-xl border border-slate-200 bg-white text-left transition-all hover:border-black hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)]"
            >
              <span className={`w-1.5 shrink-0 ${bar}`} data-testid={`release-accent-${r.id}`} />
              <div className="flex flex-1 items-center gap-5 p-4">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                  {r.cover_url ? <img src={r.cover_url} alt={r.title} className="h-full w-full object-cover" /> : <Music className="h-6 w-6 text-slate-400" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="truncate font-serif text-lg font-bold text-black">{r.title}</h3>
                    <StatusBadge map={RELEASE_STATUS} status={r.status} fallbackLabel={r.status_label} />
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                    <span>{r.main_artist || r.artist_name}</span>
                    {scope === "all" && r.artist_name && r.main_artist && r.artist_name !== r.main_artist && <span className="text-slate-400">· cuenta {r.artist_name}</span>}
                    {r.genre_main && <span>· {r.genre_main}</span>}
                    <span>· {fmtDate(r.release_date)}</span>
                    {r.isrc && <span className="font-mono">· ISRC {r.isrc}</span>}
                  </div>
                  {isAction && (
                    <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-red-600">
                      <AlertTriangle className="h-3.5 w-3.5" /> Toca para ver el problema y corregir
                    </p>
                  )}
                </div>
                {!isLive && r.status !== "takedown" && (scope === "mine" || isAdmin) && (
                  <button onClick={(e) => removeRelease(r, e)} title="Eliminar (solo si nunca llegó a la distribuidora)" data-testid={`delete-release-${r.id}`}
                    className="shrink-0 rounded-lg p-2 text-slate-300 transition-colors hover:bg-red-50 hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
                {isDraft ? (
                  <span data-testid={`continue-draft-${r.id}`} className="flex shrink-0 items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                    <Pencil className="h-3.5 w-3.5" /> Continuar edición
                  </span>
                ) : isAction ? (
                  <span data-testid={`view-problem-${r.id}`} className="flex shrink-0 items-center gap-1.5 rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white">
                    <AlertTriangle className="h-3.5 w-3.5" /> Ver problema
                  </span>
                ) : isLive ? (
                  <span data-testid={`explore-track-${r.id}`} className="flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition-colors group-hover:border-black group-hover:bg-black group-hover:text-white">
                    <Radio className="h-3.5 w-3.5" /> Explorar ficha
                  </span>
                ) : (
                  <ChevronRight className="h-5 w-5 shrink-0 text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-black" />
                )}
              </div>
            </div>
          );
        })}
        {!shown.length && <p className="rounded-xl border border-dashed border-slate-300 bg-white px-5 py-10 text-center text-sm text-slate-400">Sin lanzamientos en esta categoría.</p>}
      </div>
      </>)}

      {selected && (
        <TrackPanel
          release={selected}
          userId={user.id}
          onClose={() => setSelected(null)}
          onOpenDetail={(path) => { setSelected(null); navigate(path); }}
        />
      )}

      {/* Rejected / action-required note dialog */}
      {note && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="release-note-modal">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setNote(null)} />
          <div className="relative z-10 w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-6 py-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-100 text-red-600"><AlertTriangle className="h-5 w-5" /></div>
                <div>
                  <h3 className="font-serif text-lg font-bold text-black">Acción requerida</h3>
                  <p className="truncate text-xs text-slate-500">{note.title}</p>
                </div>
              </div>
              <button data-testid="note-modal-close" onClick={() => setNote(null)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-black"><X className="h-5 w-5" /></button>
            </div>
            <div className="px-6 py-5">
              {note.loading ? (
                <div className="flex items-center gap-2 text-sm text-slate-500"><span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-black" /> Cargando motivo…</div>
              ) : note.text ? (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="note-modal-text">{note.text}</div>
              ) : (
                <p className="text-sm text-slate-600">El equipo revisó tu entrega y requiere ajustes. Revisa la portada (3000×3000, sin marcas no autorizadas) y los metadatos, y vuelve a enviar.</p>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50/60 px-6 py-4">
              <button data-testid="note-view-detail" onClick={() => navigate(`/catalog/${note.id}`)} className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium hover:border-black">
                <ExternalLink className="h-4 w-4" /> Ver detalle
              </button>
              <button data-testid="note-fix-resubmit" onClick={() => navigate(`/catalog/${note.id}`)} className="flex items-center gap-1.5 rounded-lg bg-[#AC3333] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#FF0505]">
                <Pencil className="h-4 w-4" /> Corregir y Re-enviar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
